/**
 * KOLOM `SIAPA` DI JEJAK AUDIT — siapa yang dapat disebut, dan apa yang ditulis ketika
 * tidak ada yang dapat disebut.
 *
 * MENGAPA BERKAS INI ADA — cacat 4, temuan pemilik proyek 28 September 2026
 * -----------------------------------------------------------------------
 * Untuk peristiwa `SESSION_OPENED` dan `CREDENTIAL_READ`, kolom pelaku berbunyi
 * `Belum masuk` di baris NAMA dan `Belum masuk` LAGI di baris peran. Bentuk lamanya satu
 * baris JSX:
 *
 *     {row.actor_name ?? t(`actorKind.${row.actor_kind}`)}
 *     <br /><span className="card__muted">{t(`actorKind.${row.actor_kind}`)}</span>
 *
 * Ketika `actor_name` NULL, kedua baris menjadi label yang SAMA. Secara teknis benar —
 * peristiwanya memang terjadi sebelum sesi ada — tetapi tiga hal salah sekaligus:
 *
 *   1. Ia MENGULANG dirinya: dua baris untuk satu hal.
 *   2. "Belum masuk" berbunyi seperti KEGAGALAN. Yang sebenarnya terjadi adalah orangnya
 *      BERHASIL masuk, dan `SESSION_OPENED` adalah langkah DI DALAM keberhasilan itu.
 *      Seorang auditor yang membaca kolom itu pada hari perselisihan menyimpulkan yang
 *      sebaliknya dari yang terjadi.
 *   3. Ia ada di halaman yang justru dibuat untuk dibaca orang, tempat kalimat yang salah
 *      baca berbiaya paling mahal.
 *
 * APA YANG TIDAK BOLEH DILAKUKAN PERBAIKANNYA
 * ------------------------------------------
 * Mengarang identitas. Baris audit itu memang TIDAK menyimpan nama — pada saat
 * `SESSION_OPENED` ditulis, `app_session` belum ada, jadi tidak ada pelaku untuk dirujuk.
 * Menebaknya dari peristiwa berikutnya akan membuat jejak audit mengatakan sesuatu yang ia
 * tidak ketahui, dan sebuah jejak audit yang menebak adalah jejak audit yang tidak dapat
 * dipakai sebagai bukti. Yang diperbaiki adalah NASKAHNYA, bukan datanya.
 *
 * KELASNYA LEBIH BESAR DARIPADA `ANONYMOUS`
 * ----------------------------------------
 * Pengulangan dua baris terjadi pada SETIAP pelaku tanpa nama, termasuk `SYSTEM`. Fungsi di
 * bawah karena itu menangani kelasnya: pelaku tanpa nama memperoleh SATU baris, dan hanya
 * `ANONYMOUS` — yang keadaannya benar-benar perlu dijelaskan — memperoleh baris kedua yang
 * MENJELASKAN alih-alih mengulang.
 */

export type PelakuJejak =
  | {
      jenis: 'bernama';
      /** Nama yang benar-benar tersimpan di baris audit. */
      nama: string;
      /** Kunci i18n peran/jenis aktornya, untuk baris kedua. */
      peranKunci: string;
    }
  | {
      jenis: 'tanpaNama';
      /** Kunci i18n untuk baris pertama. */
      namaKunci: string;
      /** Kunci i18n baris kedua, atau `null` bila tidak ada baris kedua. */
      keteranganKunci: string | null;
    };

export function pelakuJejakAudit(baris: {
  actor_name?: string | null;
  actor_kind: string;
}): PelakuJejak {
  const nama = baris.actor_name?.trim();
  if (nama) {
    return { jenis: 'bernama', nama, peranKunci: `actorKind.${baris.actor_kind}` };
  }

  /**
   * `ANONYMOUS` adalah satu-satunya keadaan yang MENUNTUT penjelasan, karena ia satu-satunya
   * yang pembacanya akan salah tafsirkan. "Sistem" tanpa nama sudah menjelaskan dirinya;
   * "tidak ada sesi" tidak.
   *
   * PENJELASANNYA TIDAK DI BARIS — DAN ITU KEPUTUSAN YANG DIAMBIL DARI MELIHAT HASILNYA.
   * Bentuk pertama perbaikan ini menaruh kalimat penjelas di baris KEDUA setiap baris
   * audit. Ia benar kalimat demi kalimat, dan salah ketika dilihat: tangkapan layar Jejak
   * Audit memperlihatkan kalimat yang SAMA terulang empat puluh kali berturut-turut,
   * sehingga tabel yang seharusnya dibaca menjadi dinding teks. Cacat aslinya adalah dua
   * baris yang mengulang dirinya; mengganti pengulangan dua baris dengan pengulangan empat
   * puluh baris bukan perbaikan.
   *
   * Penjelasannya karena itu berdiri SEKALI, sebagai catatan kaki tabel
   * (`platform.audit.actor.preSessionNote`), dan hanya ketika ada baris seperti itu.
   * `adaBarisTanpaSesi()` di bawah yang menjawabnya.
   */
  if (baris.actor_kind === 'ANONYMOUS') {
    return {
      jenis: 'tanpaNama',
      namaKunci: 'platform.audit.actor.preSession',
      keteranganKunci: null,
    };
  }

  return {
    jenis: 'tanpaNama',
    namaKunci: `actorKind.${baris.actor_kind}`,
    keteranganKunci: null,
  };
}

/**
 * Benar bila daftar yang SEDANG TERLIHAT memuat sekurangnya satu baris pra-sesi.
 *
 * Catatan kakinya dirender hanya ketika ini benar. Sebuah catatan yang menjelaskan sesuatu
 * yang tidak ada di layar adalah baris yang harus dibaca dan dibuang setiap kali — dan
 * layar ini sudah punya satu catatan tetap di atasnya (jejak yang hanya dapat ditambah).
 */
export function adaBarisTanpaSesi(rows: Array<{ actor_kind: string }>): boolean {
  return rows.some((r) => r.actor_kind === 'ANONYMOUS');
}
